import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {number | null} Shared scroll position while any dialog holds the body lock */
  static #scrollLockY = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {HTMLElement | null} 모달을 연 시점에 포커스되어 있던 요소 (닫을 때 포커스 회귀용) */
  #triggerElement = null;

  /**
   * Whether another dialog element (besides this one) is currently open.
   * @param {HTMLElement} dialog
   */
  #hasOtherOpenDialogs(dialog) {
    return Array.from(document.querySelectorAll('[ref="dialog"][open]')).some((el) => el !== dialog);
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.hasAttribute('open')) return;

    // 네이티브 showModal()과 달리 닫을 때 포커스가 자동 복원되지 않으므로
    // 모달을 연 트리거 요소를 기억해 둔다
    this.#triggerElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const alreadyLocked = document.body.style.position === 'fixed';
    const lockedScrollY =
      DialogComponent.#scrollLockY ??
      (Math.abs(Number.parseInt(document.body.style.top || '0', 10)) || 0);
    const scrollY = alreadyLocked ? lockedScrollY : window.scrollY;

    this.#previousScrollY = scrollY;
    if (!alreadyLocked) {
      DialogComponent.#scrollLockY = scrollY;
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (!alreadyLocked) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.setAttribute('open', '');

      // 네이티브 showModal()과 달리 포커스가 자동으로 이동하지 않으므로
      // ESC 키 핸들링을 위해 다이얼로그로 포커스를 옮긴다
      if (!dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }
      dialog.focus();

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('pointerdown', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.hasAttribute('open')) return;

    this.removeEventListener('pointerdown', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    const keepScrollLock = this.#hasOtherOpenDialogs(dialog);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (!keepScrollLock) {
      const scrollY = DialogComponent.#scrollLockY ?? this.#previousScrollY;
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      DialogComponent.#scrollLockY = null;
      window.scrollTo({ top: scrollY, behavior: 'instant' });
    }

    dialog.removeAttribute('open');
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());

    // 모달을 연 트리거 요소로 포커스 회귀
    if (this.#triggerElement?.isConnected) {
      this.#triggerElement.focus();
    }
    this.#triggerElement = null;
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.hasAttribute('open')) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Whether ESC / backdrop click should close the dialog.
   * Loading overlays stay open until `closeDialog()` / `closeModal()` is called.
   */
  #isUserDismissible() {
    return this.refs.dialog?.dataset?.modalType !== 'loading';
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    if (!this.#isUserDismissible()) return;

    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    if (!this.#isUserDismissible()) return;
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
